// =====================================================
// MINISTER SEARCH COMBOBOX
// Stage 2: Participants & Pricing
// Searchable combobox for selecting ministers
// =====================================================

import { useState } from 'react';
import { Check, ChevronsUpDown, Search, UserCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMinisterSearch } from '../hooks/useMinisterSearch';
import { useCheckRegistration } from '../hooks/useEventParticipants';

// ===================
// TYPES
// ===================

interface MinisterSearchComboboxProps {
  eventId: string;
  value?: string;
  onSelect: (ministerId: string) => void;
  disabled?: boolean;
}

// ===================
// COMPONENT
// ===================

export function MinisterSearchCombobox({
  eventId,
  value,
  onSelect,
  disabled,
}: MinisterSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const { searchQuery, setSearchQuery, ministers, isLoading } = useMinisterSearch();

  // Check if selected minister is already registered
  const { data: isAlreadyRegistered } = useCheckRegistration(eventId, value, undefined, !!value);

  // Find selected minister
  const selectedMinister = ministers.find((m) => m.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {selectedMinister ? (
            <span className="flex items-center gap-2">
              <span>{selectedMinister.full_name}</span>
              <span className="text-xs text-muted-foreground">({selectedMinister.adb_code})</span>
              {isAlreadyRegistered && (
                <Badge variant="destructive" className="text-xs">
                  Ya registrado
                </Badge>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">Buscar ministro...</span>
          )}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 size-4 shrink-0 opacity-50" />
            <Input
              placeholder="Buscar por nombre, código o DNI..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <CommandList>
            {isLoading && (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                Buscando ministros...
              </div>
            )}
            {!isLoading && searchQuery.length < 2 && (
              <CommandEmpty>Escribe al menos 2 caracteres para buscar</CommandEmpty>
            )}
            {!isLoading && searchQuery.length >= 2 && ministers.length === 0 && (
              <CommandEmpty>No se encontraron ministros</CommandEmpty>
            )}
            {!isLoading && ministers.length > 0 && (
              <CommandGroup>
                {ministers.map((minister) => {
                  const isRegistered =
                    minister.id !== value &&
                    // Check with server if this minister is registered
                    false; // We'll do this per-minister if needed

                  return (
                    <CommandItem
                      key={minister.id}
                      value={minister.id}
                      onSelect={(currentValue: string) => {
                        onSelect(currentValue === value ? '' : currentValue);
                        setOpen(false);
                        setSearchQuery('');
                      }}
                      disabled={isRegistered}
                    >
                      <Check
                        className={cn(
                          'mr-2 size-4',
                          value === minister.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-1 items-center justify-between gap-2">
                        <div className="flex flex-col">
                          <span className="font-medium">{minister.full_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {minister.adb_code} • DNI: {minister.dni}
                          </span>
                        </div>
                        {isRegistered && (
                          <Badge variant="secondary" className="text-xs">
                            <UserCheck className="mr-1 size-3" />
                            Registrado
                          </Badge>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
